import { useEffect, useRef, useState } from 'react'

import aesEcb from 'aes-ecb'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useGetVerifyMyAgeUrl } from 'api/verification'
import { IconMasterCard } from 'assets/svg/sprite'
import { FormikSelect } from 'components/Form/FormikSelect/FormikSelect'
import InputCard from 'components/Form/InputCard/InputCard'
import MonthYearInput from 'components/Form/MonthYearInput/MonthYearInput'
import { Checkbox } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import { type AddCardInfoRequestDto, type PrefillData } from 'types/api/payment'
import discoverLogo from 'assets/images/discover.png'
import mastercardLogo from 'assets/images/mastercard.png'
import visaLogo from 'assets/images/visa.png'
import {
  findCountry,
  findRegion,
  getCountryCode,
  getCountryOptions,
  getRegionCode,
  getRegionOptions
} from 'utils/countryCatalog'
import { Formik } from 'formik'
import './_credit-card.scss'

const loadGoogleMapsScript = async (apiKey?: string) =>
  await new Promise<void>((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('Missing Google Maps API key'))
      return
    }

    if (
      typeof window?.google?.maps !== 'undefined' &&
      typeof window.google.maps.importLibrary === 'function'
    ) {
      resolve()
      return
    }

    const existing = document.querySelector(
      'script[data-google-maps-script="true"]'
    )

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('Google Maps script failed to load')),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`
    script.async = true
    script.defer = true
    script.setAttribute('data-google-maps-script', 'true')
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google Maps script failed to load'))

    document.head.appendChild(script)
  })

const countryOptions = getCountryOptions()

const getSelectedCountryId = (country: string): number | null => {
  return findCountry(country)?.id ?? null
}

const getSelectedRegionId = (
  country: string,
  state: string
): number | null => {
  const selectedCountry = findCountry(country)

  if (!selectedCountry) return null

  return findRegion(selectedCountry, state)?.id ?? null
}

const validatePostalCode = (zip: string, country: string): boolean => {
  const countryCode = getCountryCode(country)
  const value = zip.trim().toUpperCase()

  if (!value) return false

  // United States: exactly 5 numbers (12345)
  if (countryCode === 'US') {
    return /^\d{5}$/.test(value)
  }

  // Canada: LNL NLN (A1A 1A1)
if (countryCode === 'CA') {
  return /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/.test(value)
}

  // Other countries: non-empty postal code
  return true
}
const normalizePostalCode = (zip: string, country: string): string => {
  const value = zip.trim().toUpperCase()

  if (getCountryCode(country) === 'CA') {
    const cleaned = value.replace(/[\s-]/g, '')
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
  }

  return value
}

const isMapsReady = () =>
  typeof window?.google?.maps !== 'undefined' &&
  typeof window.google.maps.importLibrary === 'function'

interface CreditCardModalProps {
  editFn?: (_e: any, _id: string) => void | Promise<void>
  addNewFn?: (_dto: AddCardInfoRequestDto | any) => void | Promise<void>
  prefillData: PrefillData
}

type Step = 'card' | 'verification'

type FormDataState = {
  id: string | null
  name: string
  cardNumber: string
  expirationMonth: string
  expirationYear: string
  cvc: string
  address: string
  city: string
  state: string
  zip: string
  country: string
}

type FormErrors = {
  name: boolean
  cardNumber: boolean
  expirationMonth: boolean
  expirationYear: boolean
  expiration: boolean
  cvc: boolean
  address: boolean
  city: boolean
  state: boolean
  zip: boolean
  country: boolean
}

type ParsedPlace = {
  street: string
  city: string
  stateLong: string
  zip: string
  countryLong: string
}

type AddressSuggestion = {
  id: string
  mainText: string
  secondaryText: string
  fullText: string
  prediction: any
}

const initialFormData: FormDataState = {
  id: null,
  name: '',
  cardNumber: '',
  expirationMonth: '',
  expirationYear: '',
  cvc: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
}

const initialErrors: FormErrors = {
  name: false,
  cardNumber: false,
  expirationMonth: false,
  expirationYear: false,
  expiration: false,
  cvc: false,
  address: false,
  city: false,
  state: false,
  zip: false,
  country: false,
}

const getAddressPart = (components: any[] = [], type: string) =>
  components.find(
    component => Array.isArray(component.types) && component.types.includes(type)
  )

const getLongName = (components: any[] = [], type: string) =>
  getAddressPart(components, type)?.long_name ??
  getAddressPart(components, type)?.longText ??
  ''

const getPlaceComponents = (place: any): any[] => {
  if (Array.isArray(place?.address_components)) return place.address_components
  if (Array.isArray(place?.addressComponents)) return place.addressComponents
  return []
}

const parsePlace = (place: any): ParsedPlace => {
  const components = getPlaceComponents(place)
  const streetNumber = getLongName(components, 'street_number')
  const route = getLongName(components, 'route')

  return {
    street: [streetNumber, route].filter(Boolean).join(' ').trim(),
    city:
      getLongName(components, 'locality') ||
      getLongName(components, 'postal_town') ||
      getLongName(components, 'sublocality'),
    stateLong: getLongName(components, 'administrative_area_level_1'),
    zip: getLongName(components, 'postal_code'),
    countryLong: getLongName(components, 'country'),
  }
}

const getPredictionText = (value: any) => value?.text ?? ''

const CreditCardModal = ({
  prefillData,
  addNewFn,
  editFn,
}: CreditCardModalProps) => {
  const { t } = useTranslation()
  const modalData = useModalContext()
  const userData = useUserProfile()!
  const getVMAUrl = useGetVerifyMyAgeUrl()

  const isEditMode = Boolean(editFn && prefillData?.id)
  const todayDate = new Date()
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  const [formData, setFormData] = useState<FormDataState>(initialFormData)
  const [errors, setErrors] = useState<FormErrors>(initialErrors)
  const [confirmation, setConfirmation] = useState(false)
  const [currentStep, setCurrentStep] = useState<Step>('card')
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isMapsScriptReady, setIsMapsScriptReady] = useState(false)

  const [cardNumberTouched, setCardNumberTouched] = useState(false)
  const [expirationTouched, setExpirationTouched] = useState(false)

  const [apiError, setApiError] = useState('')

  const [addressQuery, setAddressQuery] = useState('')
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([])
  const [isAddressLoading, setIsAddressLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const addressInputRef = useRef<HTMLInputElement | null>(null)
  const sessionTokenRef = useRef<any>(null)
  const requestSeqRef = useRef(0)
  const autocompleteRef = useRef<HTMLDivElement | null>(null)
  const skipNextAddressFetchRef = useRef(false)

  const validateForm = (data: FormDataState): FormErrors => {
    const month = data.expirationMonth.trim()
    const year = data.expirationYear.trim()

    const parsedMonth = Number(month)
    const parsedYear = Number(year)
    const currentYear = todayDate.getFullYear() % 100
    const currentMonth = todayDate.getMonth() + 1

    const monthInvalid = !month || parsedMonth < 1 || parsedMonth > 12
    const yearInvalid = !year || year.length < 2 || Number.isNaN(parsedYear)

    let expirationInvalid = false
    if (!monthInvalid && !yearInvalid) {
      expirationInvalid =
        parsedYear < currentYear ||
        (parsedYear === currentYear && parsedMonth < currentMonth)
    }

    return {
      name: !isEditMode && (
        data.name.trim().length > 100 ||
        !/^[\p{L}\p{M}]+(?:[ '\u2019-][\p{L}\p{M}]+)*$/u.test(data.name.trim())
      ),
      cardNumber: !isEditMode && !/^\d{16}$/.test(data.cardNumber.trim()),
      expirationMonth: !isEditMode && monthInvalid,
      expirationYear: !isEditMode && yearInvalid,
      expiration: !isEditMode && (!monthInvalid && !yearInvalid ? expirationInvalid : false),
      cvc: !isEditMode && !/^\d{3,4}$/.test(data.cvc.trim()),
      address: !data.address.trim(),
      city: !data.city.trim(),
      state: !data.state.trim(),
      zip: !validatePostalCode(data.zip, data.country),
      country: !data.country.trim(),
    }
  }

  const getValidationErrorLabels = (nextErrors: FormErrors) => {
    const list: string[] = []
    if (nextErrors.name) list.push(t('cardholderName'))
    if (nextErrors.cardNumber) list.push(t('cardNumber'))
    if (nextErrors.expirationMonth || nextErrors.expirationYear || nextErrors.expiration) {
      list.push(t('expiration'))
    }
    if (nextErrors.cvc) list.push(t('cvc'))
    if (nextErrors.address) list.push(t('addressLine1'))
    if (nextErrors.city) list.push(t('city'))
    if (nextErrors.state) list.push(t('stateProvince'))
    if (nextErrors.zip) list.push(t('zipPostal'))
    if (nextErrors.country) list.push(t('country'))
    return list
  }

  const updateForm = (updater: (prev: FormDataState) => FormDataState) => {
    setFormData(prev => {
      const next = updater(prev)
      if (hasTriedSubmit) {
        setErrors(validateForm(next))
      }
      return next
    })
  }

  const handleFieldChange = <K extends keyof FormDataState>(
    field: K,
    value: FormDataState[K]
  ) => {
    updateForm(prev => ({ ...prev, [field]: value }))
  }

  const fetchAddressSuggestions = async (input: string) => {
    const trimmed = input.trim()

    if (!trimmed || !isMapsReady()) {
      setAddressSuggestions([])
      setShowSuggestions(false)
      setIsAddressLoading(false)
      return
    }

    const requestId = ++requestSeqRef.current
    setIsAddressLoading(true)

    try {
      const placesLib = (await google.maps.importLibrary('places')) as any

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken()
      }

      const request: any = {
        input: trimmed,
        sessionToken: sessionTokenRef.current,
        language: 'en',
      }

      const response =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      if (requestId !== requestSeqRef.current) return

      const mapped: AddressSuggestion[] = (response?.suggestions ?? [])
        .map((suggestion: any, index: number) => {
          const prediction = suggestion?.placePrediction
          if (!prediction) return null

          return {
            id: `${prediction.placeId ?? 'prediction'}-${index}`,
            mainText: getPredictionText(prediction.mainText),
            secondaryText: getPredictionText(prediction.secondaryText),
            fullText: getPredictionText(prediction.text),
            prediction,
          }
        })
        .filter(Boolean) as AddressSuggestion[]

      setAddressSuggestions(mapped)
      setShowSuggestions(mapped.length > 0)
    } catch (error) {
      console.error('[AddressAutocomplete] failed to fetch suggestions', error)
      setAddressSuggestions([])
      setShowSuggestions(false)
    } finally {
      if (requestId === requestSeqRef.current) {
        setIsAddressLoading(false)
      }
    }
  }

  const handleAddressSuggestionSelect = async (suggestion: AddressSuggestion) => {
    try {
      const place = suggestion.prediction.toPlace()

      await place.fetchFields({
        fields: ['addressComponents'],
      })

      const parsed = parsePlace(place)

      skipNextAddressFetchRef.current = true

      updateForm(prev => {
        const country = parsed.countryLong
          ? findCountry(parsed.countryLong)
          : undefined

        const countryName = country?.name ?? parsed.countryLong ?? prev.country
        const region = parsed.stateLong
          ? getRegionCode(countryName, parsed.stateLong)
          : ''

        return {
          ...prev,
          address: parsed.street || prev.address,
          city: parsed.city || prev.city,
          state: region || parsed.stateLong || prev.state,
          zip: parsed.zip || prev.zip,
          country: countryName
        }
      })

      setAddressQuery(parsed.street || suggestion.mainText || suggestion.fullText)
      setAddressSuggestions([])
      setShowSuggestions(false)
      sessionTokenRef.current = null

      if (addressInputRef.current) {
        addressInputRef.current.blur()
      }
    } catch (error) {
      console.error('[AddressAutocomplete] failed to fetch place details', error)
    }
  }

  const buildDto = (active: boolean) => {
    return {
      email: userData.email,
      street: formData.address,
      city: formData.city,
      state: getRegionCode(formData.country, formData.state),
      country: getCountryCode(formData.country),
      country_id: getSelectedCountryId(formData.country),
      region_id: getSelectedRegionId(formData.country, formData.state),
      post_code: normalizePostalCode(formData.zip, formData.country),
      card_holder: formData.name,
      card_number: formData.cardNumber,
      expiration_year: formData.expirationYear,
      expiration_month: formData.expirationMonth,
      cvc: formData.cvc,
      active,
    }
  }

  const runValidation = () => {
    const nextErrors = validateForm(formData)
    setHasTriedSubmit(true)
    setErrors(nextErrors)
    return nextErrors
  }

  const scrollToFirstError = (nextErrors: FormErrors) => {
    const fieldOrder: Array<keyof FormErrors> = [
      'name',
      'cardNumber',
      'expirationMonth',
      'expirationYear',
      'expiration',
      'cvc',
      'address',
      'city',
      'state',
      'zip',
      'country',
    ]

    const firstInvalid = fieldOrder.find(field => nextErrors[field])

    if (!firstInvalid) return

    const target =
      firstInvalid === 'cvc'
        ? document.querySelector('.creditcardmodal input[maxlength="4"]')
        : document.querySelector(
            `[data-card-field="${firstInvalid}"] input, ` +
            `[data-card-field="${firstInvalid}"] .formikInput`
          )

    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }


  const submitCard = async () => {
    if (isSubmitting) return

    setApiError('')
    const nextErrors = runValidation()

    if (Object.values(nextErrors).some(Boolean)) {
      requestAnimationFrame(() => {
        scrollToFirstError(nextErrors)
      })

      return
    }

    if (!isEditMode && !confirmation) {
      return
    }

    if (editFn && prefillData?.id) {
      try {
        setIsSubmitting(true)

        await Promise.resolve(
          editFn(
            {
              street: formData.address,
              city: formData.city,
              state: getRegionCode(formData.country, formData.state),
              country: getCountryCode(formData.country),
              country_id: getSelectedCountryId(formData.country),
              region_id: getSelectedRegionId(formData.country, formData.state),
              post_code: normalizePostalCode(formData.zip, formData.country),
              card_holder: formData.name,
              card_number: formData.cardNumber,
              expiration_year: formData.expirationYear,
              expiration_month: formData.expirationMonth,
              cvc: formData.cvc,
            },
            prefillData.id
          )
        )

        modalData.clearModal()
      } catch (error) {
        console.error('[EditCard] failed', error)
      } finally {
        setIsSubmitting(false)
      }

      return
    }

    if (!addNewFn) {
      console.warn('[AddCard] addNewFn is undefined')
      return
    }

  try {
    setIsSubmitting(true)

    const response: any = await Promise.resolve(addNewFn(buildDto(true)))

    const payload = response?.data ?? response

    if (payload?.status === 'pending_verification') {
      setCurrentStep('verification')
      return
    }

    modalData.clearModal()
} catch (error: any) {
  console.error('[AddCard] failed', error)

  const response = error?.response?.data

  const message =
    response?.error?.errors ??
    response?.error?.message ??
    response?.errors ??
    response?.message

  if (typeof message === 'string') {
    setApiError(message)
  } else if (message && typeof message === 'object') {
    const messages = Object.values(message)
      .flat()
      .filter((value): value is string => typeof value === 'string')

    setApiError(messages.join(' ') || 'Unable to save your card.')
  } else {
    setApiError(
      'Unable to save your card. Please check your details and try again.'
    )
  }
} finally {
  setIsSubmitting(false)
}
}
  const submitVerification = async () => {
    if (isVerifying || getVMAUrl.isLoading) return

    try {
      setIsVerifying(true)
      const url = await getVMAUrl.mutateAsync()
      window.location.assign(url)
    } catch (error) {
      // console.error('[Verification] failed', error)
      setIsVerifying(false)
    }
  }

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!autocompleteRef.current?.contains(target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [])

  useEffect(() => {
    let mounted = true

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (mounted) {
          setIsMapsScriptReady(true)
        }
      })
      .catch(error => {
        console.error('[AddressAutocomplete] Google Maps script load failed', error)
        if (mounted) {
          setIsMapsScriptReady(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [GOOGLE_MAPS_API_KEY])

  useEffect(() => {
    if (!prefillData) {
      setFormData(initialFormData)
      setErrors(initialErrors)
      setCurrentStep('card')
      setAddressQuery('')
      setAddressSuggestions([])
      setShowSuggestions(false)
      return
    }

    const nextState: FormDataState = {
      id: prefillData.id ? prefillData.id : null,
      name: prefillData.name ?? '',
      cardNumber: prefillData.cardNumber ?? '',
      expirationMonth: '',
      expirationYear: '',
      cvc: '',
      address: prefillData.address ?? '',
      city: prefillData.city ?? '',
      state: prefillData.state ?? '',
      zip: prefillData.zip ?? '',
      country: prefillData.country ?? '',
    }

    if (prefillData.expiration) {
      const [month, year] = prefillData.expiration.split('/')
      nextState.expirationMonth = month ?? ''
      nextState.expirationYear = year ?? ''
    }

    if (prefillData.cvc) {
      try {
        nextState.cvc = aesEcb.decrypt(
          'ShVmYq3t6w9z$C&F)J@McQfTjWnZr4u7',
          prefillData.cvc
        )
      } catch {
        nextState.cvc = ''
      }
    }

    setFormData(nextState)
    setErrors(initialErrors)
    setCurrentStep('card')
    setAddressQuery(nextState.address)
  }, [prefillData])

  useEffect(() => {
    if (addressInputRef.current && addressInputRef.current.value !== formData.address) {
      addressInputRef.current.value = formData.address
    }

    if (addressQuery !== formData.address) {
      setAddressQuery(formData.address)
    }
  }, [addressQuery, formData.address])

  useEffect(() => {
    if (!isMapsScriptReady || !addressQuery.trim()) {
      setAddressSuggestions([])
      setShowSuggestions(false)
      setIsAddressLoading(false)
      return
    }

    if (skipNextAddressFetchRef.current) {
      skipNextAddressFetchRef.current = false
      return
    }

    const timeoutId = setTimeout(() => {
      fetchAddressSuggestions(addressQuery)
    }, 220)

    return () => clearTimeout(timeoutId)
  }, [addressQuery, isMapsScriptReady])

  const primaryButtonLabel = isSubmitting ? t('loading') : t('saveCard')
  const isNarrowScreen = window.matchMedia('(max-width: 399px)').matches
  const expirationErrors = validateForm(formData)

  const expirationInvalid =
    expirationErrors.expirationMonth ||
    expirationErrors.expirationYear ||
    expirationErrors.expiration
    
  return (
    <div className="creditcardmodal">
      <Form onSubmit={e => e.preventDefault()}>
        {currentStep === 'card' ? (
          <>
            <Info>
              <div className="creditcardmodal__name-field">
                <InputCard
                  type="string"
                  value={formData.name}
                  label={t('cardholderName')}
                  maxChars={100}
                  disabled={isEditMode}
                  changeFn={(val: string) =>
                    handleFieldChange('name', val)
                  }
                  inputProps={{
                    onBlur: () => {
                      const name = formData.name.trim()

                      setErrors(prev => ({
                        ...prev,
                        name:
                          name.length > 0 &&
                          !/^[\p{L}\p{M}]+(?:[ '\u2019-][\p{L}\p{M}]+)*$/u.test(name)
                      }))
                    }
                  }}
                  hasError={() => undefined}
                  customClass={
                    (hasTriedSubmit || formData.name.trim().length > 0) && errors.name
                      ? 'inputcard--error'
                      : ''
                  }
                />

                {errors.name && formData.name.trim().length > 0 && (
                  <div className="creditcardmodal__name-error" role="alert">
                    Cardholder name may only contain letters, spaces,
                    hyphens, and apostrophes.
                  </div>
                )}
              </div>

              <InputCard
                type="string"
                value={formData.cardNumber}
                disabled={isEditMode}
                maxChars={16}
                label={t('cardNumber')}
                hasIcon={true}
                icon={<IconMasterCard width="30" />}
                iconRight={true}
                iconSmaller={true}
                changeFn={(val: string) =>
                  handleFieldChange('cardNumber', val.replace(/[^0-9]+/g, ''))
                }
                inputProps={{
                  onBlur: () => setCardNumberTouched(true)
                }}
                hasError={() => undefined}
                customClass={
                  !isEditMode &&
                  (
                    (hasTriedSubmit || (cardNumberTouched && formData.cardNumber.length > 0)) &&
                    !/^\d{16}$/.test(formData.cardNumber.trim())
                  )
                    ? 'inputcard--error'
                    : ''
                }
              />


              {!isEditMode && (
                <>
                  <FormGroup   onBlur={e => {
    const nextFocused = e.relatedTarget as Node | null

    if (
      e.target instanceof HTMLElement &&
      e.target.closest('.month-year-input-wrapper') &&
      !e.currentTarget.contains(nextFocused)
    ) {
      setExpirationTouched(true)
    }
  }}>
                    <MonthYearInput
                      month={formData.expirationMonth}
                      year={formData.expirationYear}
monthChangeFn={(val: any) => {
  setExpirationTouched(false)
  handleFieldChange(
    'expirationMonth',
    String(val).replace(/[^0-9]+/g, '')
  )
}}
yearChangeFn={(val: any) => {
  setExpirationTouched(false)
  handleFieldChange(
    'expirationYear',
    String(val).replace(/[^0-9]+/g, '')
  )
}}
                      minYear={todayDate.getFullYear() % 100}
                      maxYear={(todayDate.getFullYear() + 10) % 100}
                      placeholder="MM / YY"
                      label={t('expiration')}
                      handleError={() => undefined}
hasError={
  (expirationTouched || hasTriedSubmit) &&
  expirationInvalid &&
  (hasTriedSubmit ||
    !!formData.expirationMonth ||
    !!formData.expirationYear)
}
                    />

                    <InputCard
                      maxChars={4}
                      type="string"
                      value={formData.cvc}
                      label={t('cvc')}
                      changeFn={(val: string) =>
                        handleFieldChange('cvc', val.replace(/[^0-9]+/g, ''))
                      }
                      customClass={
                        hasTriedSubmit && errors.cvc ? 'inputcard--error' : ''
                      }
                    />
                  </FormGroup>

{(expirationTouched || hasTriedSubmit) &&
  expirationInvalid &&
  (hasTriedSubmit ||
    !!formData.expirationMonth ||
    !!formData.expirationYear) && (
    <div
      className="creditcardmodal__expiration-error"
      role="alert"
    >
      Invalid Date
    </div>
  )}
                </>
              )}

            </Info>

            <ModalTitle>{t('billingAddress')}</ModalTitle>

            <ModalSection>
              <FullRow>
                <AutocompleteField ref={autocompleteRef}>
                  <InputCard
                    type="string"
                    value={addressQuery}
                    label={`${t('address')}`}
                    changeFn={(val: string) => {
                      skipNextAddressFetchRef.current = false
                      setAddressQuery(val)
                      handleFieldChange('address', val)
                    }}
                    hasError={() => undefined}
                    customClass={hasTriedSubmit && errors.address ? 'inputcard--error' : ''}
                    externalInputRef={addressInputRef}
                    inputProps={{
                      autoComplete: 'off',
                      onFocus: () => {
                        if (addressSuggestions.length > 0) {
                          setShowSuggestions(true)
                        }
                      },
                    }}
                  />

                  {showSuggestions && (addressSuggestions.length > 0 || isAddressLoading) && (
                    <SuggestionsDropdown>
                      {isAddressLoading && addressSuggestions.length === 0 ? (
                        <SuggestionLoading>Searching...</SuggestionLoading>
                      ) : (
                        addressSuggestions.map(item => (
                          <SuggestionItem
                            key={item.id}
                            type="button"
                            onMouseDown={e => {
                              e.preventDefault()
                              handleAddressSuggestionSelect(item)
                            }}
                          >
                            <SuggestionMain>{item.mainText || item.fullText}</SuggestionMain>
                            {!!item.secondaryText && (
                              <SuggestionSecondary>{item.secondaryText}</SuggestionSecondary>
                            )}
                          </SuggestionItem>
                        ))
                      )}
                    </SuggestionsDropdown>
                  )}
                </AutocompleteField>
              </FullRow>

              <InputCard
                type="string"
                value={formData.city}
                label={t('city')}
                changeFn={(val: any) => handleFieldChange('city', String(val))}
                hasError={() => undefined}
                customClass={hasTriedSubmit && errors.city ? 'inputcard--error' : ''}
              />
            <CountrySelectWrapper
              $hasCountry={!!formData.country}
              data-card-field="country"
              className={
                hasTriedSubmit && errors.country
                  ? 'inputcard--error'
                  : ''
              }
            >
              <Formik
                initialValues={{
                  country: getCountryCode(formData.country)
                }}
                enableReinitialize
                onSubmit={() => {}}
              >
                {({ setFieldValue }) => (
                  <FormikSelect
                    fieldName="country"
                    label={t('country')}
                    options={countryOptions}
                    selectedValue={getCountryCode(formData.country)}
                    searchable
                    autoPosition
                    autoComplete="country"
                    onChange={selected => {
                      const country = findCountry(selected.value)

                      setFieldValue('country', selected.value)

                      updateForm(prev => ({
                        ...prev,
                        country: country?.name ?? '',
                        state: ''
                      }))
                    }}
                    onTouched={() => {}}
                  />
                )}
              </Formik>
            </CountrySelectWrapper>

              <div data-card-field="state"  className={
                hasTriedSubmit && errors.state
                  ? 'inputcard--error'
                  : ''
              }>
                <Formik
                  initialValues={{
                    state: getRegionCode(formData.country, formData.state)
                  }}
                  enableReinitialize
                  onSubmit={() => {}}
                >
                  {({ setFieldValue }) => (
                    <FormikSelect
                      fieldName="state"
                      label={isNarrowScreen ? 'State/Prov' : t('stateProvince')}
                      options={getRegionOptions(formData.country)}
                      selectedValue={getRegionCode(formData.country, formData.state)}
                      autoComplete="address-level1"
                      autoPosition
                      onChange={selected => {
                        const regionCode =
                          getRegionCode(formData.country, selected.value) ||
                          selected.value

                        setFieldValue('state', regionCode)

                        handleFieldChange('state', regionCode)
                      }}
                      onTouched={() => {}}
                    />
                  )}
                </Formik>
              </div>
              <div data-card-field="zip">
                <InputCard
                  type="string"
                  value={formData.zip}
                  label={t('zipPostal')}
                  maxChars={
                    getCountryCode(formData.country) === 'US'
                      ? 5
                      : getCountryCode(formData.country) === 'CA'
                        ? 7
                        : 20
                  }
                  changeFn={(val: string) => {
                    const countryCode = getCountryCode(formData.country)

                    if (countryCode === 'US') {
                      handleFieldChange('zip', val.replace(/\D/g, '').slice(0, 5))
                      return
                    }

                    if (countryCode === 'CA') {
                      const cleaned = val
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, '')
                        .slice(0, 6)

                      handleFieldChange(
                        'zip',
                        cleaned.length > 3
                          ? `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
                          : cleaned
                      )
                      return
                    }

                    handleFieldChange('zip', val)
                  }}
                  hasError={() => undefined}
                  customClass={hasTriedSubmit && errors.zip ? 'inputcard--error' : ''}
                />
              </div>
            </ModalSection>

            {!isEditMode && (
              <CheckboxContainer>
                <Checkbox
                  id="18-check"
                  label={<CheckboxText>{t('confirmThatYouAreAtLeast18')}</CheckboxText>}
                  value="confirmation"
                  checked={confirmation}
                  handleOnChange={() => setConfirmation(!confirmation)}
                />
              </CheckboxContainer>
            )}

          {apiError && (
            <ApiError role="alert">
              {apiError}
            </ApiError>
          )}

          <SubmitButton
            type="button"
            value={primaryButtonLabel}
            onClick={submitCard}
            disabled={isSubmitting}
          />

          <PaymentTrustBlock>
            <CardBrands>
              <img src={visaLogo} alt="Visa" />
              <img src={mastercardLogo} alt="Mastercard" />
              <img src={discoverLogo} alt="Discover" />
            </CardBrands>

            <SecureText>
              Secure transaction
            </SecureText>

            <MerchantInfo>
              <strong>AllFans</strong>
              <span>2108 N ST, STE N</span>
              <span>SACRAMENTO, CA 95816</span>
            </MerchantInfo>
          </PaymentTrustBlock>
          </>
        ) : (
          <>
            <Info>
              <Paragraph>
                We could not save your card directly. Please complete age verification to
                continue.
              </Paragraph>
            </Info>

            <SubmitButton
              type="button"
              disabled={isVerifying || getVMAUrl.isLoading}
              value={
                isVerifying || getVMAUrl.isLoading
                  ? t('redirecting')
                  : t('submitVerification')
              }
              onClick={submitVerification}
            />
          </>
        )}
      </Form>
    </div>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.silver};
  border-radius: 2rem;
  padding: 2rem 2rem 1rem;
  margin: 0 1rem;
`

const ModalTitle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1.6rem;
  color: ${colors.blueDark};
  padding: 2.5rem;

  svg {
    margin-right: 1rem;
  }
`

const FormGroup = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const ModalSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0rem 1rem;
  padding: 0 1rem;
  align-items: start;
`

const FullRow = styled.div`
  grid-column: 1 / -1;
`

const CheckboxContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 2rem;
`

const CheckboxText = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey};
`

const Paragraph = styled.p`
  font-size: 1.4rem;
  line-height: 1.6;
  color: ${colors.blueDark};
  margin: 0;
`
const ApiError = styled.div`
  width: 92%;
  margin: 1rem auto 0;
  padding: 1.2rem 1.5rem;
  border-radius: 1.2rem;
  background: #fff1f1;
  color: #c62828;
  font-size: 1.3rem;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  overflow-wrap: anywhere;
`
const SubmitButton = styled.input<{ disabled?: boolean }>`
  display: block;
  width: 92%;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
  color: ${colors.white};
  border: none;
  background: ${colors.blue92deg};
  box-shadow: ${colors.shadowBlue};
  border-radius: 2.5rem;
  margin: 1.5rem auto 0;
  margin-bottom:1rem;
  cursor: pointer;
  padding: 1.5rem 4.5rem;
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  pointer-events: ${p => (p.disabled ? 'none' : 'auto')};
`

const AutocompleteField = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
`

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.6rem);
  left: 0;
  right: 0;
  background: ${colors.white};
  border: 0.1rem solid #d9dde5;
  border-radius: 1.6rem;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.08);
  max-height: 28rem;
  overflow-y: auto;
  z-index: 20;
`

const SuggestionItem = styled.button`
  width: 100%;
  display: block;
  text-align: left;
  background: transparent;
  border: none;
  padding: 1.2rem 1.4rem;
  cursor: pointer;

  &:hover {
    background: #f6f8fb;
  }
`

const SuggestionMain = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.blueDark};
`

const SuggestionSecondary = styled.div`
  font-size: 1.25rem;
  color: ${colors.grey};
  margin-top: 0.2rem;
`

const SuggestionLoading = styled.div`
  padding: 1.2rem 1.4rem;
  font-size: 1.3rem;
  color: ${colors.grey};
`
const PaymentTrustBlock = styled.div`
  width: 92%;
  margin: 2.5rem auto 1rem;
  padding-top: 2rem;
  border-top: 0.1rem solid #e5e7eb;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  font-family: 'Montserrat', sans-serif;
`

const CardBrands = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  img {
    height: 3rem;
    width: auto;
    object-fit: contain;
    display: block;
  }
`

const SecureText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.grey};
`
const CountrySelectWrapper = styled.div<{ $hasCountry: boolean }>`
  width: 100%;
  min-width: 0;
  position: relative;

  ${({ $hasCountry }) =>
    $hasCountry &&
    `
      .formikInput > label {
        top: 1.2rem;
        transform: none;
      }

.formikInput > div[class*='searchableControl'] {
  top: 2.5rem;
}
    `}
`
const MerchantInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-weight:500;
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${colors.grey};

  strong {
    color: ${colors.blueDark};
    font-weight: 600;
  }
`
export default CreditCardModal